import ConsoleImproved from '../../shared/classes/ConsoleImproved';
import { PublicUserDataV2, ResolveState, TaskMeetingData } from '../../shared/types/types';
import { InviteSource, SHARE_NOTES_EMAIL, SLACK } from '../../utils/analytics/enums';
import { REJECTED, RESOLVED } from '../../utils/enums';
import EmailAPI from '../Email/EmailAPI';
import cfSearchPublicUserDataV2ByEmail from '../publicUserData/PublicUserDataAPI';
import SlackAPI from '../Slack/SlackAPI';

class MentionAPI {
  /**
   * @param receiverEmail email of the person that was mentioned,
   * and also the person to receive the notification
   * @param reporterEmail email of the person who mentioned the person
   * @param authUserEmail email of the logged in user. If the reporter is the same as auth,
   * then we don't send any notifications
   * @param simpleMeetingData meeting data of the meeting that the person was mentioned in
   * @param content OBS: Not in use right now.
   * The content to be delivered with the mention. This should be the
   * sentence that the person was mentioned in.
   * i.e. `@haraldlons what are your thoughts on the above?`
   * Then the content should be `what are your thoughts on the above?`
   */
  static async createMention(
    receiverEmail: string,
    reporterEmail: string,
    authUserEmail: string,
    simpleMeetingData: TaskMeetingData,
    content: string,
  ): Promise<ResolveState> {
    ConsoleImproved.log('MentionAPI, ', {
      receiverEmail, reporterEmail, authUserEmail, content, simpleMeetingData,
    });
    if (receiverEmail === authUserEmail) {
      ConsoleImproved.log('MentionAPI, receiverEmail is the same as authUserEmail');
      return RESOLVED;
    }
    const reporter = await cfSearchPublicUserDataV2ByEmail(reporterEmail);
    const receiver = await cfSearchPublicUserDataV2ByEmail(receiverEmail);
    const slackNotification = MentionAPI.sendSlackNotificaiton(receiver,
      reporter, simpleMeetingData);
    const emailNotificaiton = MentionAPI.sendEmailNotification(
      receiverEmail, reporter, simpleMeetingData,
    );
    return Promise.all([slackNotification, emailNotificaiton])
      .then(() => RESOLVED as ResolveState)
      .catch(() => REJECTED as ResolveState);
  }

  private static async sendSlackNotificaiton(
    receiver: PublicUserDataV2,
    reporter: PublicUserDataV2,
    simpleMeetingData: TaskMeetingData,
  ): Promise<ResolveState> {
    if (receiver.external.slack.hasEnabledSlack === false
      || receiver.external.slack.notifications.mentionedInNotes === false) {
      ConsoleImproved.log('MentionAPI, slack notificaiton is disabled', { reporter: receiver });
      return RESOLVED;
    }

    const reporterName = reporter.isShepherdUser ? reporter.data.firstName : reporter.data.email;
    const title = `${reporterName} mentioned you in "${simpleMeetingData.name}"\n\n`;
    const message = `${title} <${MentionAPI.createSlackMeetingLink(simpleMeetingData.meetingId)}|Click here> to view and edit the notes`;
    return SlackAPI.sendNotification(title, message, receiver);
  }

  private static async sendEmailNotification(
    receiverEmail: string,
    reporter: PublicUserDataV2,
    simpleMeetingData: TaskMeetingData,
  ): Promise<ResolveState> {
    // TODO: We should have own setting for receiving email notifications for mentions
    if (reporter.external.email.receiveTaskEmail === false) {
      ConsoleImproved.log('MentionAPI, email notificaiton is disabled', { reporter });
      return RESOLVED;
    }

    const reporterName = reporter.isShepherdUser ? reporter.data.firstName : reporter.data.email;
    const subject = `${reporterName} mentioned you in "${simpleMeetingData.name}"`;
    const body = MentionAPI.createMentionEmailBody(reporterName, simpleMeetingData);
    return EmailAPI.sendBasicEmail([receiverEmail], subject, body);
  }

  static createMentionEmailBody(reporterName: string, simpleMeetingData: TaskMeetingData) {
    let body = `${reporterName} mentioned you in "${simpleMeetingData.name}". `;
    const clickHereLink = MentionAPI.createHtmlLink('Click here', MentionAPI.createMeetingLink(SHARE_NOTES_EMAIL, simpleMeetingData.meetingId));
    body += `${clickHereLink} to view and edit the notes`;
    return body;
  }

  static createHtmlLink(text: string, url: string) {
    return `<a href="${url}">${text}</a>`;
  }

  static createMeetingLink(channel: InviteSource, meetingId: string) {
    return `https://app.meetshepherd.com/meeting/${meetingId}?ref=${channel}`;
  }

  static createSlackMeetingLink(meetingId: string) {
    const url = `${MentionAPI.createMeetingLink(SLACK, meetingId)}`;
    return url;
  }
}

export default MentionAPI;
